import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost';
type Size = 'md' | 'sm' | 'icon';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

const variants: Record<Variant, string> = {
  primary:
    'bg-brand-red text-fg hover:bg-brand-redAlt disabled:bg-surface-raised disabled:text-fg-subtle',
  secondary:
    'bg-transparent text-fg border border-surface-divider hover:bg-surface-raised disabled:text-fg-subtle disabled:border-surface-divider',
  ghost:
    'bg-transparent text-fg-muted hover:text-fg hover:bg-surface-raised disabled:text-fg-subtle',
};

const sizes: Record<Size, string> = {
  md: 'h-11 px-6 text-sm font-medium rounded-lg',
  sm: 'h-9 px-4 text-sm font-medium rounded-lg',
  icon: 'h-9 w-9 rounded-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', className, ...rest }, ref) => (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center gap-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2 focus-visible:ring-offset-surface-bg disabled:cursor-not-allowed',
        variants[variant],
        sizes[size],
        className,
      )}
      {...rest}
    />
  ),
);
Button.displayName = 'Button';
